/**
 * Single source of truth for the persisted user session.
 *
 * localStorage('user') is the canonical store; every read/write must go
 * through these helpers. Spreading a stale in-memory copy over the stored
 * user has caused session clobbering twice (see MEMORY: dashboardStore
 * clobber) — mergeStoredUser always re-reads storage as the merge base.
 */

const KEY = 'user';

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function saveStoredUser(user) {
  localStorage.setItem(KEY, JSON.stringify(user));
  return user;
}

/** Merge updates over the FRESH stored user and persist. Returns the merged user. */
export function mergeStoredUser(updates) {
  const merged = { ...(getStoredUser() || {}), ...updates };
  return saveStoredUser(merged);
}

export function clearStoredUser() {
  localStorage.removeItem(KEY);
}

/**
 * Is this session token still inside its lifetime? Decodes the payload only
 * (base64url JSON with `exp`, see backend/shared/auth-token.js) — the signature
 * is the server's business. Used for UX decisions, never for trust.
 */
export function isTokenLive(token, now = Date.now()) {
  if (typeof token !== 'string') return false;
  const dot = token.indexOf('.');
  if (dot <= 0) return false;
  try {
    const b64 = token.slice(0, dot).replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64 + '==='.slice((b64.length + 3) % 4)));
    return typeof payload.exp === 'number' && payload.exp > now;
  } catch (e) {
    return false;
  }
}

/** Username for auth headers — supports both session shapes. */
export function getUsername(user) {
  return user?.Tg_Username || user?.username || '';
}
